// Gatherly's data layer: accounts, invitations and replies, kept by the
// Gatherly API (backend/). Every page goes through these functions.
import { getEventType } from "../data/eventTypes";
import { getToken, isNotFound, request, setSession } from "./api";

// Ids the app gives new invitations: 6–64 lowercase letters and digits, as
// the API requires.
export const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const now = () => new Date().toISOString();

/* ------------------------------------------------------------------ auth */

// Every sign-in answers { token, refreshToken, expiresAt, user }.
const startSession = (session) => {
  setSession(session);
  return session.user;
};

const anonymous = { anonymous: true };

// details: { name, email, password, lang }. The API emails a link to confirm
// the address (to /verify-email).
export const registerUser = async (details) => startSession(await request("POST", "/auth/register", details, anonymous));

export const loginUser = async (credentials) => startSession(await request("POST", "/auth/login", credentials, anonymous));

// Ends the session on the server too, so the token can't be used again.
export const logoutUser = async () => {
  if (getToken()) await request("POST", "/auth/logout").catch(() => {});
  setSession(null);
};

// The signed-in host, or null. A session the API no longer accepts is ended
// by request() itself.
export const getCurrentUser = async () => {
  if (!getToken()) return null;
  try {
    return await request("GET", "/auth/me");
  } catch {
    return null;
  }
};

// changes: { name, phone, password?, currentPassword? }. A new password needs
// the current one (unless the host signed in within the last few minutes);
// it signs out the host's other devices.
export const updateUser = (changes) => request("PUT", "/auth/me", changes);

// Always succeeds, whether or not the email has an account; if it has, a
// link to /reset-password is emailed.
export const requestPasswordReset = (email) => request("POST", "/auth/password/forgot", { email }, anonymous);

// token comes from the emailed link. Signs out every device.
export const resetPassword = (token, password) =>
  request("POST", "/auth/password/reset", { token, password }, anonymous);

export const verifyEmail = (token) => request("POST", "/auth/email/verify", { token }, anonymous);

export const resendVerification = () => request("POST", "/auth/email/resend");

// Links in emails carry their token after "#" (it never reaches server logs).
export const tokenFromHash = (hash) => new URLSearchParams(hash.replace(/^#/, "")).get("token") || "";

/* ---------------------------------------------------------------- events */

export const createDraft = (ownerId, type = "") => ({
  id: uid(),
  ownerId,
  type,
  templateId: "",
  status: "draft",
  slug: "",
  title: "",
  hosts: "",
  date: "",
  time: "",
  venue: "",
  address: "",
  message: "",
  dressCode: "",
  rsvpDeadline: "",
  // Photos and music are stored by services/media.js; events keep their ids.
  coverId: "",
  gallery: [],
  story: [],
  schedule: [],
  music: { mediaId: "", url: "", title: "" },
  // Cover video: a bundled clip (stockId), an upload (mediaId) or a link (url).
  video: { stockId: "", mediaId: "", url: "" },
  backdrop: "auto",
  look: { colorway: "default", gender: "neutral" },
  showCountdown: true,
  settings: {
    allowPlusOnes: true,
    maxGuests: 2,
    mealsEnabled: type ? getEventType(type).mealsByDefault : false,
    meals: [],
    askDietary: true,
    askMessage: true,
    questions: [],
  },
  payment: null,
  createdAt: now(),
  updatedAt: now(),
});

// Invitations saved before a field existed get that field's default.
export const normalize = (event) => {
  const defaults = createDraft(event.ownerId);
  return {
    ...defaults,
    ...event,
    music: { ...defaults.music, ...event.music },
    video: { ...defaults.video, ...event.video },
    look: { ...defaults.look, ...event.look },
    settings: { ...defaults.settings, ...event.settings },
  };
};

export const mediaIds = (event) =>
  [
    event.coverId,
    event.music?.mediaId,
    event.video?.mediaId,
    ...(event.gallery || []),
    ...(event.story || []).map((chapter) => chapter.photoId),
  ].filter(Boolean);

// An invitation's status is "draft", "published" (paid, guests can open it)
// or "suspended" (paid, but taken offline by Gatherly).
export const isPaid = (event) => event.status === "published" || event.status === "suspended";

export const listEvents = async () => (await request("GET", "/events")).map(normalize);

const orNull = (promise) =>
  promise.then(normalize).catch((error) => {
    if (isNotFound(error)) return null;
    throw error;
  });

export const getEvent = (id) => orNull(request("GET", `/events/${id}`));

export const getEventBySlug = (slug) => orNull(request("GET", `/public/events/${encodeURIComponent(slug)}`));

export const saveEvent = async (event) => normalize(await request("PUT", `/events/${event.id}`, event));

export const deleteEvent = (id) => request("DELETE", `/events/${id}`);

// The demo checkout: saves the latest wording, then publishes the invitation
// with its public link. Only the chosen method is sent (the API ignores it
// and takes the price from its own list); refused once PayFast is on.
export const publishEvent = async (event, payment) => {
  await saveEvent(event);
  return normalize(await request("POST", `/events/${event.id}/publish`, { method: payment?.method }));
};

// How invitations are paid for: "demo" (the built-in demo checkout) or
// "payfast" (the host is sent to PayFast; see backend/internal/payments).
export const getPaymentConfig = () => request("GET", "/payments/config").catch(() => ({ mode: "demo" }));

// Saves the latest wording, then gets the signed PayFast form for it.
export const startCheckout = async (event) => {
  await saveEvent(event);
  return request("POST", `/events/${event.id}/checkout`);
};

// Sends the browser to PayFast with the signed fields.
export const goToPayfast = ({ action, fields }) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  fields.forEach(({ name, value }) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

export const invitationPath = (event) => `/i/${event.slug}`;

export const invitationUrl = (event) => `${window.location.origin}${invitationPath(event)}`;

/* ----------------------------------------------------------------- rsvps */

export const listRsvps = (eventId) => request("GET", `/events/${eventId}/rsvps`);

export const deleteRsvp = (id) => request("DELETE", `/rsvps/${id}`);

// Brings back a reply removed moments ago (the "Undo" after deleting).
export const restoreRsvp = (rsvp) => request("POST", `/rsvps/${rsvp.id}/restore`);

// This browser remembers which reply it sent for each invitation, with the
// private token that lets the guest change it later.
const MINE_KEY = "gatherly_my_rsvps";

const readMine = () => {
  try {
    return JSON.parse(localStorage.getItem(MINE_KEY)) || {};
  } catch {
    return {};
  }
};

const rememberMine = (eventId, entry) => {
  try {
    localStorage.setItem(MINE_KEY, JSON.stringify({ ...readMine(), [eventId]: entry }));
  } catch {
    // ignore storage failures
  }
};

export const getMyRsvp = async (eventId) => {
  const mine = readMine()[eventId];
  if (!mine?.id || !mine?.token) return null;
  try {
    return await request("GET", `/public/rsvps/${mine.id}`, undefined, { headers: { "X-Edit-Token": mine.token } });
  } catch {
    return null;
  }
};

export const submitRsvp = async (event, data) => {
  const mine = readMine()[event.id];
  if (mine?.id && mine?.token) {
    try {
      return await request("PUT", `/public/rsvps/${mine.id}`, data, { headers: { "X-Edit-Token": mine.token } });
    } catch (error) {
      if (!isNotFound(error)) throw error;
      // The host removed the earlier reply: send it as a new one.
    }
  }
  const { rsvp, editToken } = await request("POST", `/public/events/${encodeURIComponent(event.slug)}/rsvps`, data);
  rememberMine(event.id, { id: rsvp.id, token: editToken });
  return rsvp;
};

// Headcount only counts guests who said yes; meals are tallied for those
// same guests.
export const summarize = (rsvps) => {
  const summary = { responses: rsvps.length, yes: 0, no: 0, maybe: 0, headcount: 0, meals: {} };
  rsvps.forEach((rsvp) => {
    summary[rsvp.status] = (summary[rsvp.status] || 0) + 1;
    if (rsvp.status !== "yes") return;
    const guests = rsvp.guests || [];
    summary.headcount += guests.length || 1;
    guests.forEach((guest) => {
      if (guest.mealId) summary.meals[guest.mealId] = (summary.meals[guest.mealId] || 0) + 1;
    });
  });
  return summary;
};
